import { useState, useEffect, useMemo, type ReactNode } from 'react'
import { X, GitCommit, ChevronRight, Clock, FileText, Plus, Minus, Filter, GitCompare } from 'lucide-react'
import { useHistory } from '../hooks/useWorkspace'
import { timeAgo, formatExactTime } from '../utils/writerUtils'
import type { CommitSummary, AffectedFile } from '../engine/types'
import './HistoryPanel.css'

function changeIcon(changeType: AffectedFile['changeType']): ReactNode {
    if (changeType === 'added') return <Plus size={10} />
    if (changeType === 'deleted') return <Minus size={10} />
    return <GitCompare size={10} />
}

function commitAuthorLabel(commit: CommitSummary): string {
    const name = commit.writerName?.trim()
    if (name) return name
    return commit.writerKid.slice(0, 8)
}

function commitTimeLabel(commit: CommitSummary): string {
    if (!commit.time) return ''
    return timeAgo(commit.time, { maxDepth: 'days', suffix: ' ago' })
}

interface HistoryPanelProps {
    isOpen: boolean
    onClose: () => void
    onDiffFile?: (path: string, commit: CommitSummary) => void
    onBrowseSnapshot?: (commit: CommitSummary) => void
    filePath?: string | null
    initialMode?: 'all' | 'file'
}

export default function HistoryPanel({ isOpen, onClose, onDiffFile, onBrowseSnapshot, filePath, initialMode = 'all' }: HistoryPanelProps) {
    const { commits, listForFile } = useHistory()
    const [expandedCommit, setExpandedCommit] = useState<string | null>(null)
    const [selectedCommit, setSelectedCommit] = useState<string | null>(null)
    const resolvedInitialMode = initialMode === 'file' && filePath ? 'file' : 'all'
    const [filterMode, setFilterMode] = useState<'all' | 'file'>(resolvedInitialMode)

    useEffect(() => {
        if (isOpen) {
            setExpandedCommit(null)
            setSelectedCommit(null)
            setFilterMode(resolvedInitialMode)
        }
    }, [isOpen, resolvedInitialMode])

    // Derive filtered commits from the engine
    const filteredCommits = useMemo(() => {
        if (filterMode === 'file' && filePath) return listForFile(filePath)
        return commits
    }, [filterMode, filePath, commits, listForFile])

    // Unique contributors
    const contributorCount = useMemo(() => {
        const kids = new Set(commits.map(c => c.writerKid))
        return kids.size
    }, [commits])

    if (!isOpen) return null

    const fileName = filePath?.split('/').pop()
    const emptyStateLabel = filterMode === 'file' && fileName
        ? `No history for ${fileName} yet`
        : 'No workspace history yet'

    return (
        <>
            <div className="history-backdrop" onClick={onClose} />
            <div className="history-panel">
                <div className="history-header">
                    <h2><Clock size={18} /> History</h2>
                    <button className="btn btn-icon" onClick={onClose}><X size={18} /></button>
                </div>

                {/* Dual-mode filter tabs */}
                {filePath && (
                    <div className="history-filter-bar">
                        <button
                            className={`history-filter-btn ${filterMode === 'all' ? 'active' : ''}`}
                            onClick={() => setFilterMode('all')}
                        >
                            All history
                        </button>
                        <button
                            className={`history-filter-btn ${filterMode === 'file' ? 'active' : ''}`}
                            onClick={() => setFilterMode('file')}
                        >
                            <Filter size={12} />
                            {fileName}
                        </button>
                    </div>
                )}
                {filterMode === 'file' && fileName && (
                    <div className="history-filter-chip-row">
                        <div className="history-filter-chip">
                            <Filter size={12} />
                            <span>History for {fileName}</span>
                            <button
                                type="button"
                                className="history-filter-chip-clear"
                                onClick={() => setFilterMode('all')}
                                aria-label={`Clear history filter for ${fileName}`}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    </div>
                )}

                <div className="history-timeline">
                    {filteredCommits.length === 0 && (
                        <div className="history-empty">
                            <FileText size={24} />
                            <span>{emptyStateLabel}</span>
                        </div>
                    )}
                    {filteredCommits.map((commit, i) => (
                        <div key={commit.commitHash} className="history-commit-wrapper">
                            {/* Timeline connector */}
                            <div className="history-timeline-track">
                                <div className={`history-timeline-dot ${i === 0 ? 'latest' : ''}`}>
                                    <GitCommit size={14} />
                                </div>
                                {i < filteredCommits.length - 1 && <div className="history-timeline-line" />}
                            </div>

                            {/* Commit card */}
                            <div
                                className={`history-commit ${expandedCommit === commit.commitHash ? 'expanded' : ''} ${selectedCommit === commit.commitHash ? 'selected' : ''}`}
                                onClick={() => {
                                    const nextExpanded = expandedCommit === commit.commitHash ? null : commit.commitHash
                                    setExpandedCommit(nextExpanded)
                                    setSelectedCommit(commit.commitHash)
                                }}
                            >
                                <div className="history-commit-header">
                                    <span className="history-commit-avatar">{commit.writerEmoji ?? '👤'}</span>
                                    <div className="history-commit-meta">
                                        <span className="history-commit-author">{commitAuthorLabel(commit)}</span>
                                        {commit.time ? (
                                            <span className="history-commit-time" title={formatExactTime(commit.time)}>
                                                {commitTimeLabel(commit)}
                                            </span>
                                        ) : null}
                                    </div>
                                    <code className="history-commit-hash">{commit.commitHash.slice(0, 8)}</code>
                                    {commit.parents.length > 1 && (
                                        <span className="history-commit-badge merge" title="Merge commit">Merge</span>
                                    )}
                                    <ChevronRight size={14} className={`history-chevron ${expandedCommit === commit.commitHash ? 'rotated' : ''}`} />
                                </div>

                                <div className="history-commit-message">
                                    {commit.message?.trim() || `${commit.affectedFiles.length} file${commit.affectedFiles.length !== 1 ? 's' : ''} changed`}
                                </div>

                                {expandedCommit === commit.commitHash && (
                                    <>
                                        <div className="history-commit-actions">
                                            <button
                                                type="button"
                                                className="history-commit-action"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onBrowseSnapshot?.(commit)
                                                }}
                                            >
                                                Browse snapshot
                                            </button>
                                        </div>
                                        {commit.affectedFiles.length > 0 && (
                                            <div className="history-commit-files">
                                                {commit.affectedFiles.map(file => (
                                                    <div
                                                        key={file.path}
                                                        className={`history-file ${filePath === file.path ? 'highlighted' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onDiffFile?.(file.path, commit)
                                                            onClose()
                                                        }}
                                                        title="Compare with current version"
                                                    >
                                                        <span className={`history-file-change-icon ${file.changeType}`}>
                                                            {changeIcon(file.changeType)}
                                                        </span>
                                                        <span className="history-file-info">
                                                            <span className="history-file-path">{file.path}</span>
                                                            <span className="history-file-cta">Diff with current</span>
                                                        </span>
                                                        <span className={`history-file-change-type ${file.changeType}`}>
                                                            {file.changeType}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="history-footer">
                    <span>
                        {filteredCommits.length} commit{filteredCommits.length !== 1 ? 's' : ''}
                        {filterMode === 'file' && fileName && ` · ${fileName}`}
                        {filterMode === 'all' && ` · ${contributorCount} contributor${contributorCount !== 1 ? 's' : ''}`}
                    </span>
                </div>
            </div>
        </>
    )
}
